import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../Styles/MahaShakthiTable.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { mahaShakthiAPI } from "../../api/index";
import {
  FaBus,
  FaCheckCircle,
  FaRegClock,
  FaSearch,
  FaMapMarkerAlt,
  FaFilter,
  FaSyncAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaEdit,
  FaTrashAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaTicketAlt,
  FaUpload,
  FaDownload,
  FaImage,
} from "react-icons/fa";

// Kept only for the filter dropdown / Add-Edit mandal <select> options.
// No longer used to derive display values — the API returns
// mandal_name / village_name directly, so we trust those.
const MANDAL_NAMES = ["Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = { pending: "Pending", approved: "Approved", rejected: "Rejected", disbursed: "Disbursed" };

// ---------- Excel template settings ----------
// Headings follow the Add Beneficiary form fields (same order as the form).
// CONFIRM these with the backend's bulk-upload parser. If it expects different
// heading names, change them here only.
const TEMPLATE_HEADERS = [
  "Beneficiary Name",
  "Relation",
  "Age",
  "Aadhaar Number",
  "Mobile Number",
  "Bus Pass Number",
  "Preferred Route",
  "Depot",
  "Mandal",
  "Village",
  "Date",
  "Status",
  "Photo URL",
  "Supporting Document URL",
  "Remarks",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 28 }, // Beneficiary Name
  { wch: 22 }, // Relation
  { wch: 8 }, // Age
  { wch: 18 }, // Aadhaar Number
  { wch: 16 }, // Mobile Number
  { wch: 20 }, // Bus Pass Number
  { wch: 28 }, // Preferred Route
  { wch: 24 }, // Depot
  { wch: 20 }, // Mandal
  { wch: 22 }, // Village
  { wch: 14 }, // Date
  { wch: 14 }, // Status
  { wch: 38 }, // Photo URL
  { wch: 38 }, // Supporting Document URL
  { wch: 40 }, // Remarks
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Beneficiary Name", "Yes", "3-100 characters. Alphabets, spaces, '.' and ''' only"],
  ["Relation", "Yes", "2-100 characters (e.g. D/O Ramesh, W/O Suresh)"],
  ["Age", "Yes", "Whole number from 1 to 120"],
  ["Aadhaar Number", "Yes", "Exactly 12 digits, cannot start with 0 or 1. Format the cell as Text in Excel"],
  ["Mobile Number", "Yes", "Exactly 10 digits, must start with 6, 7, 8 or 9. Format the cell as Text in Excel"],
  ["Bus Pass Number", "Yes", "5-20 characters. Letters, digits, '-' and '/' only"],
  ["Preferred Route", "No", "Optional. 3-100 characters (e.g. Kandukur to Ongole)"],
  ["Depot", "No", "Optional. 3-100 characters (e.g. Kandukur Depot)"],
  ["Mandal", "Yes", MANDAL_NAMES.join(", ")],
  ["Village", "Yes", "Alphabets and spaces only. Must exactly match an existing village under the Mandal"],
  ["Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Status", "Yes", STATUS_OPTIONS.join(", ")],
  ["Photo URL", "No", "Optional. A link starting with http:// or https://"],
  ["Supporting Document URL", "No", "Optional. A link starting with http:// or https://"],
  ["Remarks", "No", "Optional. If filled: 5-500 characters"],
];

const FALLBACK_IMG =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23e5e7eb'/%3E%3C/svg%3E";

const num = (v) => (typeof v === "number" ? v : Number(v) || 0);

const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || d?.data || [];
};

const extractTotalCount = (res, fallbackLen) => {
  const d = res?.data;
  return num(d?.total ?? d?.count ?? d?.total_count ?? fallbackLen);
};

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

// FIX: use the name fields the backend returns directly instead of
// re-deriving a label from mandal_id/village_id.
const normalizeRow = (row) => ({
  id: row.id,
  name: row.beneficiary_name || "Untitled",
  relation: row.relation_name || "",
  age: row.age ?? "",
  aadhaarMasked: row.aadhaar_masked || "",
  mobile: row.mobile_number || "",
  busPass: row.bus_pass_number || "",
  route: row.preferred_route || "",
  depot: row.depot || "",
  mandalId: row.mandal_id,
  mandal: row.mandal_name || (row.mandal_id ? `Mandal #${row.mandal_id}` : "—"),
  villageId: row.village_id,
  village: row.village_name || (row.village_id ? `Village #${row.village_id}` : "—"),
  date: row.application_date || "",
  status: row.status || "pending",
  photoUrl: row.photo_url || "",
  documentUrl: row.document_url || "",
  remarks: row.remarks || "",
});

const MahaShakthiTable = () => {
  const navigate = useNavigate();

  const [msData, setMsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 10;

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchMsData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: PAGE_SIZE };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (mandalFilter !== "All Mandals") params.mandal_name = mandalFilter;
      if (statusFilter !== "All Status") params.status = statusFilter;

      const res = await mahaShakthiAPI.getAll(params);
      const rows = extractList(res).map(normalizeRow);
      setMsData(rows);
      setTotalCount(extractTotalCount(res, rows.length));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, mandalFilter, statusFilter]);

  useEffect(() => {
    fetchMsData();
  }, [fetchMsData]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + msData.length;

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  const approvedCount = msData.filter((i) => i.status === "approved").length;
  const pendingCount = msData.filter((i) => i.status === "pending").length;

  // FIX: edit form now pre-fills Village/Mandal from the row directly and
  // carries every field the update payload accepts (Mobile was already
  // there; now Age/Aadhaar/Document URL/Remarks are included and
  // pre-filled too). Aadhaar is left blank and required on save since GET
  // only returns a masked value and the backend rejects updates missing
  // it (confirmed pattern across the sibling scheme tables).
  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || "",
      relation: item.relation || "",
      age: item.age ?? "",
      aadhaar: "",
      mobile: item.mobile || "",
      busPass: item.busPass || "",
      route: item.route || "",
      depot: item.depot || "",
      mandalName: item.mandal || "",
      villageName: item.village && item.village !== "—" ? item.village : "",
      date: item.date || "",
      status: item.status || "pending",
      photoUrl: item.photoUrl || "",
      documentUrl: item.documentUrl || "",
      remarks: item.remarks || "",
    });
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();

    const trimmedVillage = editForm.villageName.trim();
    const trimmedAadhaar = editForm.aadhaar.trim();

    if (!editForm.name.trim()) {
      toast.error("Beneficiary Name is required");
      return;
    }
    if (!editForm.busPass.trim()) {
      toast.error("Bus Pass Number is required");
      return;
    }
    if (!trimmedVillage) {
      toast.error("Please enter the village name to save");
      return;
    }
    if (!editForm.mandalName) {
      toast.error("Please select a Mandal");
      return;
    }
    // FIX: Aadhaar must be re-entered in full on every save — GET only
    // returns a masked value, and the update endpoint rejects requests
    // missing this field (confirmed on the sibling scheme tables).
    if (!trimmedAadhaar) {
      toast.error("Please re-enter the 12-digit Aadhaar Number to save changes");
      return;
    }
    if (trimmedAadhaar.length !== 12 || !/^\d{12}$/.test(trimmedAadhaar)) {
      toast.error("Aadhaar Number must be exactly 12 digits");
      return;
    }
    if (editForm.mobile && editForm.mobile.length !== 10) {
      toast.error("Mobile Number must be exactly 10 digits");
      return;
    }
    if (editForm.age && (Number(editForm.age) <= 0 || Number(editForm.age) > 120)) {
      toast.error("Enter a valid Age");
      return;
    }
    if (editForm.photoUrl.trim() && !isLikelyUrl(editForm.photoUrl.trim())) {
      toast.error("Enter a valid Photo URL starting with https://");
      return;
    }
    if (editForm.documentUrl.trim() && !isLikelyUrl(editForm.documentUrl.trim())) {
      toast.error("Enter a valid Document URL starting with https://");
      return;
    }

    const payload = {
      beneficiary_name: editForm.name.trim(),
      relation_name: editForm.relation.trim() || undefined,
      age: editForm.age ? Number(editForm.age) : undefined,
      // FIX: always send a real value now — never undefined.
      aadhaar_number: trimmedAadhaar,
      mobile_number: editForm.mobile.trim() || undefined,
      bus_pass_number: editForm.busPass.trim(),
      preferred_route: editForm.route.trim() || undefined,
      depot: editForm.depot.trim() || undefined,
      mandal_name: editForm.mandalName,
      village_name: trimmedVillage,
      application_date: editForm.date || undefined,
      status: editForm.status,
      photo_url: editForm.photoUrl.trim() || undefined,
      document_url: editForm.documentUrl.trim() || undefined,
      remarks: editForm.remarks.trim() || undefined,
    };

    setSaving(true);
    try {
      await mahaShakthiAPI.update(editItem.id, payload);
      toast.success("Beneficiary updated");
      closeEditModal();
      fetchMsData();
    } catch (err) {
      console.error("Maha Shakthi update payload sent:", payload);
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (item) => setDeleteItem(item);
  const closeDeleteModal = () => setDeleteItem(null);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await mahaShakthiAPI.delete(deleteItem.id);
      toast.success("Deleted successfully");
      closeDeleteModal();
      fetchMsData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const openImagePreview = (url) => setImagePreviewUrl(url);
  const closeImagePreview = () => setImagePreviewUrl(null);

  /* ---------- Download Excel template (headings only) ---------- */
  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 26 }, { wch: 10 }, { wch: 90 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "Maha_Shakthi_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  const handleBulkUploadClick = () => bulkFileInputRef.current?.click();

  const handleBulkFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    setBulkUploading(true);
    try {
      const res = await mahaShakthiAPI.bulkUpload(file);
      toast.success(`Import complete — ${num(res.data?.inserted)} beneficiaries added`);
      fetchMsData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="ms-page">
      <input
        ref={bulkFileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleBulkFileSelected}
      />

      <div className="ms-header">
        <div className="ms-header-left">
          <div className="ms-header-icon">
            <FaBus />
          </div>
          <div>
            <h2 className="ms-title">Maha Shakthi (Free Bus Travel) Beneficiaries</h2>
            <p className="ms-subtitle">Manage and track beneficiaries under Maha Shakthi Scheme</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="ms-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <span className="ms-add-plus">
              <FaDownload />
            </span>{" "}
            Download Template
          </button>

          <button
            className="ms-add-btn"
            onClick={handleBulkUploadClick}
            disabled={bulkUploading}
            style={{ background: "#0b3d24" }}
          >
            <span className="ms-add-plus">{bulkUploading ? "⏳" : <FaUpload />}</span>{" "}
            {bulkUploading ? "Uploading..." : "Bulk Upload (Excel)"}
          </button>

          <button className="ms-add-btn" onClick={() => navigate("/dashboard/MahaShakthiForm")}>
            <span className="ms-add-plus">+</span> Add New Beneficiary
          </button>
        </div>
      </div>

      <div className="ms-scroll-body">
        <div className="ms-stats-row">
          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-purple">
              <FaUserCircle />
            </div>
            <div>
              <p className="ms-stat-label">Total Beneficiaries</p>
              <h3 className="ms-stat-value">{totalCount}</h3>
              <p className="ms-stat-sub">All time records</p>
            </div>
          </div>

          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-green">
              <FaCheckCircle />
            </div>
            <div>
              <p className="ms-stat-label">Approved Beneficiaries</p>
              <h3 className="ms-stat-value">{approvedCount}</h3>
              <p className="ms-stat-sub ms-sub-green">
                {msData.length ? ((approvedCount / msData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-orange">
              <FaRegClock />
            </div>
            <div>
              <p className="ms-stat-label">Pending Beneficiaries</p>
              <h3 className="ms-stat-value">{pendingCount}</h3>
              <p className="ms-stat-sub ms-sub-orange">
                {msData.length ? ((pendingCount / msData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-blue">
              <FaTicketAlt />
            </div>
            <div>
              <p className="ms-stat-label">Total Bus Passes Issued</p>
              <h3 className="ms-stat-value">{totalCount}</h3>
              <p className="ms-stat-sub ms-sub-blue">All time records</p>
            </div>
          </div>
        </div>

        <div className="ms-filter-section">
          <div className="ms-search-wrapper">
            <FaSearch className="ms-search-icon" />
            <input
              type="text"
              placeholder="Search Beneficiary..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="ms-select-wrapper">
            <FaMapMarkerAlt className="ms-select-icon" />
            <select
              value={mandalFilter}
              onChange={(e) => {
                setMandalFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Mandals</option>
              {MANDAL_NAMES.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="ms-select-wrapper">
            <FaFilter className="ms-select-icon" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All Status">All Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>

          <button className="ms-reset-btn" onClick={handleResetFilters}>
            <FaSyncAlt /> Reset Filters
          </button>
        </div>

        <div className="ms-table-wrapper">
          <table className="ms-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Relation</th>
                <th>Bus Pass No.</th>
                <th>Route</th>
                <th>Depot</th>
                <th>Village</th>
                <th>Mandal</th>
                <th>Date</th>
                <th>Status</th>
                <th>Photo</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="ms-no-data">Loading…</td>
                </tr>
              ) : msData.length > 0 ? (
                msData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <div className="ms-name-cell">
                        <span className="ms-avatar">
                          <FaUserCircle />
                        </span>
                        <span className="ms-name-text">{item.name}</span>
                      </div>
                    </td>
                    <td className="ms-relation-cell">{item.relation}</td>
                    <td>
                      <span className="ms-buspass-badge">{item.busPass}</span>
                    </td>
                    <td>{item.route}</td>
                    <td>{item.depot}</td>
                    <td>
                      <span className="ms-village-cell">
                        <FaMapMarkerAlt /> {item.village}
                      </span>
                    </td>
                    <td>{item.mandal}</td>
                    <td>
                      <span className="ms-date-cell">
                        <FaCalendarAlt /> {item.date}
                      </span>
                    </td>
                    <td>
                      <span className={`ms-status ${item.status === "approved" ? "ms-approved" : "ms-pending"}`}>
                        {item.status === "approved" ? <FaCheckCircle /> : <FaRegClock />}{" "}
                        {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </td>
                    <td>
                      {item.photoUrl ? (
                        <button
                          type="button"
                          onClick={() => openImagePreview(item.photoUrl)}
                          title="View photo"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#16a34a",
                            cursor: "pointer",
                            fontSize: "18px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FaImage />
                        </button>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>—</span>
                      )}
                    </td>
                    <td className="ms-action-cell">
                      <button className="ms-edit-btn" onClick={() => openEditModal(item)}>
                        <FaEdit /> Edit
                      </button>
                      <button className="ms-delete-btn" onClick={() => openDeleteModal(item)}>
                        <FaTrashAlt /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="ms-no-data">No matching beneficiaries found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="ms-footer">
          <p className="ms-footer-text">
            {totalCount > 0
              ? `Showing ${startIndex + 1} to ${endIndex} of ${totalCount} beneficiaries`
              : "Showing 0 beneficiaries"}
          </p>

          <div className="ms-pagination">
            <button className="ms-page-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              <FaChevronLeft />
            </button>

            {getPageNumbers().map((page, i) =>
              page === "..." ? (
                <span key={`dots-${i}`} className="ms-page-dots">...</span>
              ) : (
                <button
                  key={page}
                  className={`ms-page-btn ${currentPage === page ? "ms-page-active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ),
            )}

            <button
              className="ms-page-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="ms-scheme-banner">
          <div className="ms-banner-icon-left">
            <FaBus />
          </div>

          <div className="ms-banner-text">
            <h4>Maha Shakthi Scheme</h4>
            <p>
              Providing free bus travel for women across Andhra Pradesh to support their daily commute, education
              and empowerment.
            </p>
          </div>
        </div>
      </div>

      {/* ---------------- EDIT MODAL — now includes ALL update fields ---------------- */}
      {editItem && editForm && (
        <div className="ms-modal-overlay" onClick={closeEditModal}>
          <div className="ms-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ms-modal-header">
              <h3>Edit Beneficiary</h3>
              <button className="ms-modal-close" onClick={closeEditModal}>
                <FaTimes />
              </button>
            </div>

            <form className="ms-modal-form" onSubmit={handleEditSave}>
              <div className="ms-form-row">
                <label>Beneficiary Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  required
                />
              </div>

              <div className="ms-form-row">
                <label>Relation</label>
                <input
                  type="text"
                  value={editForm.relation}
                  onChange={(e) => handleEditChange("relation", e.target.value)}
                />
              </div>

              {/* FIX: Aadhaar must be re-entered in full — the backend
                  rejects the whole update without it. */}
              <div className="ms-form-row">
                <label>Aadhaar Number * (re-enter to save)</label>
                <input
                  type="text"
                  maxLength="12"
                  placeholder="Enter 12-digit Aadhaar Number"
                  value={editForm.aadhaar}
                  onChange={(e) => handleEditChange("aadhaar", e.target.value)}
                  required
                />
                <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  On file: {editItem.aadhaarMasked || "—"}. The full number must be re-entered here every time — the server does not accept updates without it.
                </small>
              </div>

              <div className="ms-form-grid">
                <div className="ms-form-row">
                  <label>Age</label>
                  <input
                    type="number"
                    value={editForm.age}
                    onChange={(e) => handleEditChange("age", e.target.value)}
                  />
                </div>

                <div className="ms-form-row">
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    maxLength="10"
                    value={editForm.mobile}
                    onChange={(e) => handleEditChange("mobile", e.target.value)}
                  />
                </div>
              </div>

              <div className="ms-form-grid">
                <div className="ms-form-row">
                  <label>Bus Pass No. *</label>
                  <input
                    type="text"
                    value={editForm.busPass}
                    onChange={(e) => handleEditChange("busPass", e.target.value)}
                    required
                  />
                </div>

                <div className="ms-form-row">
                  <label>Route</label>
                  <input
                    type="text"
                    value={editForm.route}
                    onChange={(e) => handleEditChange("route", e.target.value)}
                  />
                </div>
              </div>

              <div className="ms-form-grid">
                <div className="ms-form-row">
                  <label>Depot</label>
                  <input
                    type="text"
                    value={editForm.depot}
                    onChange={(e) => handleEditChange("depot", e.target.value)}
                  />
                </div>

                <div className="ms-form-row">
                  <label>Status *</label>
                  <select value={editForm.status} onChange={(e) => handleEditChange("status", e.target.value)}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ms-form-grid">
                <div className="ms-form-row">
                  <label>Village * (re-enter to save)</label>
                  <input
                    type="text"
                    placeholder="Enter Village Name"
                    value={editForm.villageName}
                    onChange={(e) => handleEditChange("villageName", e.target.value)}
                    required
                  />
                  <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    Must exactly match an existing village name under the selected Mandal.
                  </small>
                </div>

                <div className="ms-form-row">
                  <label>Mandal *</label>
                  <select value={editForm.mandalName} onChange={(e) => handleEditChange("mandalName", e.target.value)}>
                    {MANDAL_NAMES.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ms-form-row">
                <label>Date</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => handleEditChange("date", e.target.value)}
                  required
                />
              </div>

              <div className="ms-form-row">
                <label>Photo URL</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={editForm.photoUrl}
                  onChange={(e) => handleEditChange("photoUrl", e.target.value)}
                />
              </div>

              <div className="ms-form-row">
                <label>Supporting Document URL</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={editForm.documentUrl}
                  onChange={(e) => handleEditChange("documentUrl", e.target.value)}
                />
              </div>

              {editForm.photoUrl && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={editForm.photoUrl}
                    alt="Beneficiary preview"
                    style={{
                      width: "110px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              <div className="ms-form-row" style={{ marginTop: "10px" }}>
                <label>Remarks</label>
                <textarea
                  rows="3"
                  value={editForm.remarks}
                  onChange={(e) => handleEditChange("remarks", e.target.value)}
                />
              </div>

              <div className="ms-modal-actions">
                <button type="button" className="ms-modal-cancel-btn" onClick={closeEditModal}>
                  Cancel
                </button>
                <button type="submit" className="ms-modal-save-btn" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteItem && (
        <div className="ms-modal-overlay" onClick={closeDeleteModal}>
          <div className="ms-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ms-confirm-icon">
              <FaTrashAlt />
            </div>
            <h3 className="ms-confirm-title">Delete this beneficiary?</h3>
            <p className="ms-confirm-text">
              Are you sure you want to delete <strong>{deleteItem.name}</strong> from the beneficiary list? This
              action cannot be undone.
            </p>

            <div className="ms-confirm-actions">
              <button className="ms-confirm-cancel-btn" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button className="ms-confirm-delete-btn" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {imagePreviewUrl && (
        <div className="ms-modal-overlay" onClick={closeImagePreview}>
          <div
            className="ms-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "480px", padding: 0 }}
          >
            <div className="ms-modal-header">
              <h3>Beneficiary Photo</h3>
              <button className="ms-modal-close" onClick={closeImagePreview}>
                <FaTimes />
              </button>
            </div>
            <div style={{ padding: 20, textAlign: "center" }}>
              <img
                src={imagePreviewUrl}
                alt="Beneficiary"
                style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: 8 }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = FALLBACK_IMG;
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MahaShakthiTable;